import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import ToggleSwitch from 'fe-design-base/atoms/ToggleSwitch';
import Alert from 'fe-design-base/molecules/Alert';
import { AlertVariant } from 'fe-design-base/molecules/Alert/types';
import Button from 'fe-design-base/molecules/Button';
import Divider from 'fe-design-base/molecules/Divider';
import IconButton from 'fe-design-base/molecules/IconButton';
import CircularInfinite from 'fe-design-base/molecules/Loaders/CircularInfinite';

import { getCurrentLocationName } from 'selectors/session';

import { selectPTOPolicies } from 'features/MyWeek/slice';
import {
  approveTimeOffRequest,
  getTimeOffRequestDetails,
  rejectTimeOffRequest,
} from 'features/notifications/api';
import { useRequestAction } from 'features/notifications/hooks/useRequestAction';

import { displayTime, formatDatePrettyShortWithYear } from 'util/dateTime';
import { toI18n } from 'util/i18n';

import DetailItem from './DetailItem/DetailItem';

type TimeOffDetailProps = {
  requestId: any;
  onClose: () => void;
  isTeamRequest?: boolean;
  responderName?: string;
};

const TimeOffDetail = ({
  requestId,
  isTeamRequest,
  responderName,
  onClose,
}: TimeOffDetailProps) => {
  const [timeOffDetails, setTimeOffDetails] = useState<any | null>(null);
  const ptoPolicies = useSelector(selectPTOPolicies);
  const location = useSelector(getCurrentLocationName);
  const timeOffId = requestId.replace('time_off-', '');

  useEffect(() => {
    getTimeOffRequestDetails(timeOffId).then(setTimeOffDetails);
  }, [timeOffId]);

  const { isLoading: isApproving, handleAction: handleApprove } =
    useRequestAction({
      apiFunction: approveTimeOffRequest,
      itemId: timeOffId,
      successMessage: 'requests_feed.time_off.approved_success',
      initiatedFrom: 'time_off',
      buttonText: toI18n('requests_feed.actions.approve'),
    });

  const { isLoading: isRejecting, handleAction: handleReject } =
    useRequestAction({
      apiFunction: rejectTimeOffRequest,
      itemId: timeOffId,
      successMessage: 'requests_feed.time_off.rejected_success',
      initiatedFrom: 'time_off',
      buttonText: toI18n('requests_feed.actions.deny'),
    });

  if (!timeOffDetails) {
    return (
      <Box column h="50vh" justifyContentCenter alignItemsCenter>
        <CircularInfinite size="large" />
      </Box>
    );
  }

  const requestedHours = Object.values(
    timeOffDetails.time_off_day_durations
  ).reduce((acc: number, duration: any) => acc + duration.duration_in_hours, 0);

  // Not final implementation, need to get the current balance from the backend
  const currentBalance =
    (ptoPolicies &&
      ptoPolicies.find(
        (policy: any) =>
          policy.category_name === timeOffDetails.normalized_category_name
      )?.balance) ||
    0;

  const remainingHours = Math.max(currentBalance - requestedHours, 0);

  const state = timeOffDetails.state;
  const states: Record<string, AlertVariant> = {
    pending: 'warning',
    approved: 'success',
    rejected: 'error',
  };

  const stateTexts: Record<string, string> = {
    pending: toI18n('requests_feed.time_off.details.pending'),
    approved: toI18n('requests_feed.time_off.details.approved'),
    rejected: toI18n('requests_feed.time_off.details.rejected'),
  };

  const {
    normalized_category_name: normalizedCategoryName,
    reason,
    notes,
    whole_day: wholeDay,
    start_at: startAt,
    end_at: endAt,
  } = timeOffDetails;

  return (
    <Box p={16} ph={32}>
      <Box flex row alignItemsCenter justifyContentSpaceBetween>
        <IconButton
          icon="ArrowLeft"
          label={toI18n('actions.go_back')}
          onClick={onClose}
        />
        <Text variant="bodyBold" i18n="requests_feed.time_off.details.title" />
        <div style={{ width: '40px' }} />
      </Box>

      {!isTeamRequest && (
        <Alert variant={states[state]}>{stateTexts[state]}</Alert>
      )}

      <DetailItem
        label={
          isTeamRequest
            ? toI18n('requests_feed.time_off.details.team_member')
            : toI18n('requests_feed.time_off.details.location')
        }
        value={isTeamRequest ? responderName : location}
      />
      <DetailItem
        label={toI18n('requests_feed.time_off.details.category')}
        value={normalizedCategoryName}
      />
      {reason && (
        <DetailItem
          label={toI18n('requests_feed.time_off.details.reason')}
          value={reason}
        />
      )}
      {notes?.length > 0 && (
        <DetailItem
          label={toI18n('requests_feed.time_off.details.notes')}
          value={notes}
          optional
        />
      )}
      <Divider color="mono300" spacing={16} />
      <Box row alignItemsCenter gap={8}>
        <ToggleSwitch name="whole_day" checked={wholeDay} disabled />
        <Text i18n="requests_feed.time_off.details.whole_day" />
      </Box>
      <DetailItem
        label="Start Date"
        value={formatDatePrettyShortWithYear(startAt)}
      />
      <DetailItem
        label="End Date"
        value={formatDatePrettyShortWithYear(endAt)}
      />
      {!timeOffDetails.whole_day && (
        <>
          <DetailItem label="Start Time" value={displayTime(startAt)} />
          <DetailItem label="End Time" value={displayTime(endAt)} />
        </>
      )}
      <Divider color="mono300" spacing={16} />
      <Box row alignItemsCenter justifyContentSpaceBetween>
        <Text
          variant="captionBold"
          uppercase
          i18n="requests_feed.time_off.details.total_hours_requested"
        />

        <Text
          variant="heading4"
          i18n="requests_feed.time_off.details.hours"
          i18nProps={{ hours: requestedHours }}
        />
      </Box>
      {!isTeamRequest && (
        <Box row alignItemsCenter justifyContentSpaceBetween>
          <Text
            variant="captionBold"
            uppercase
            i18n="requests_feed.time_off.details.remaining_balance"
          />

          <Text
            variant="heading4"
            i18n="requests_feed.time_off.details.hours"
            i18nProps={{ hours: remainingHours }}
          />
        </Box>
      )}
      {isTeamRequest && (
        <Box column gap={8} mv={16}>
          <Button
            fullWidth
            onClick={handleApprove}
            isLoading={isApproving}
            disabled={isApproving || isRejecting}
          >
            {toI18n('requests_feed.actions.approve')}
          </Button>
          <Button
            variant="secondary"
            fullWidth
            onClick={handleReject}
            isLoading={isRejecting}
            disabled={isRejecting || isApproving}
          >
            {toI18n('requests_feed.actions.deny')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TimeOffDetail;
